import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  CircularProgress,
  Divider,
  TextField,
  Stack,
} from "@mui/material";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import SearchIcon from "@mui/icons-material/Search";
import dayjs, { Dayjs } from "dayjs";
import { HttpAdminApi } from "../../../interface/admin-api";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface ReplaySelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (video: any) => void;
  onCancel: () => void;
  userState: any;
}

const adminApi = new HttpAdminApi();

const ReplaySelectionDialog = ({ open, onClose, onSelect, onCancel, userState }: ReplaySelectionDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [vodList, setVodList] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);

  const [srchDateFrom, setSrchDateFrom] = useState<Dayjs | null>(dayjs().subtract(90, "day"));
  const [srchDateTo, setSrchDateTo] = useState<Dayjs | null>(dayjs());
  const [srchBroadTitle, setSrchBroadTitle] = useState("");

  // 팝업이 열릴 때 VOD 리스트를 가져옵니다.
  useEffect(() => {
    if (open) {
      fetchVodList();
    }
  }, [open]);

  // 재생시간 계산 함수 (20260310110000 포맷 대응)
  const getPlayTime = (start: string, stop: string) => {
    if (!start || !stop) return "00:00:00";
    
    // YYYYMMDDHHmmss 포맷을 dayjs 객체로 변환
    const startTime = dayjs(start, "YYYYMMDDHHmmss");
    const stopTime = dayjs(stop, "YYYYMMDDHHmmss");
    
    const diffSeconds = stopTime.diff(startTime, "second");
    if (diffSeconds < 0) return "00:00:00";

    const hours = Math.floor(diffSeconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((diffSeconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = (diffSeconds % 60).toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  };

  const fetchVodList = async () => {
    if (userState.id === "" || userState.id === undefined) return;

    setLoading(true);
    try {      
      const param: any = {
        command: "get_replay_list",
        search_condition: {
          broad_date: [dayjs(srchDateFrom).format("YYYYMMDD"), dayjs(srchDateTo).format("YYYYMMDD")],
          broad_status: ["VOD"],
          sort: "DESCEND",
        },
        request_user_id: userState.id,
      };

      const res = await adminApi.post(param);
      if (res.code === "200") {
        console.log("res : ", res.response.channel_list);
        const fullList = res.response.channel_list || [];
        
        // 검색어 필터링
        const filteredList = srchBroadTitle.trim() !== "" 
          ? fullList.filter((v: any) => v.broad_title.includes(srchBroadTitle))
          : fullList;
          
        setVodList(filteredList);
      }
    } catch (error) {
      console.error("VOD 목록을 불러오는데 실패했습니다.", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedVideo) {
      onSelect(selectedVideo);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VideoLibraryIcon color="primary" />
        재방송 영상 선택
      </DialogTitle>
      <Divider />
      
      {/* 검색 필터 영역 */}
      <Box sx={{ p: 2, backgroundColor: "#f9f9f9" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label="방송시작 From"
                  format="YYYY-MM-DD"
                  value={srchDateFrom}
                  onChange={(newValue) => setSrchDateFrom(newValue)}
                  sx={{ width: "160px" }}
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  label="방송시작 To"
                  format="YYYY-MM-DD"
                  value={srchDateTo}
                  onChange={(newValue) => setSrchDateTo(newValue)}
                  sx={{ width: "160px" }}
                  slotProps={{ textField: { size: "small" } }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: "200px" }}>
            <TextField
              label="방송명 검색"
              variant="outlined"
              size="small"
              fullWidth
              value={srchBroadTitle}
              onChange={(e) => setSrchBroadTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchVodList();
              }}
            />
          </Box>
          <Button 
            variant="contained" 
            startIcon={<SearchIcon />} 
            onClick={fetchVodList}
            sx={{ height: "40px" }}
          >
            검색
          </Button>
        </Stack>
      </Box>

      <Divider />

      <DialogContent sx={{ p: 0, height: "500px", overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress />
          </Box>
        ) : vodList.length > 0 ? (
          <List>
            {vodList.map((video: any, index: number) => (
              <ListItem
                key={index}
                disablePadding
                divider
                secondaryAction={
                  <Typography variant="caption" color="text.secondary">
                    {/* 20260310110000 -> 2026-03-10 변환 */}
                    {dayjs(video.broad_start_tm, "YYYYMMDDHHmmss").format("YYYY-MM-DD")}
                  </Typography>
                }
              >
                <ListItemButton
                  selected={selectedVideo?.broad_seq === video.broad_seq}
                  onClick={() => setSelectedVideo(video)}
                >
                  <ListItemIcon>
                    <PlayCircleFilledIcon color={selectedVideo?.broad_seq === video.broad_seq ? "primary" : "action"} />
                  </ListItemIcon>
                  <ListItemText
                    primary={video.broad_title}
                    secondary={`리허설시간: ${video.time_diff}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography>불러올 수 있는 VOD 영상이 없습니다.</Typography>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
        {/* 왼쪽: 설정 취소 버튼 (이미 설정된 비디오가 있을 때 노출하면 좋음) */}
        {/* <Button 
          onClick={() => {
            if(window.confirm("설정된 재방송 영상을 취소하시겠습니까?")) {
              onCancel(); // 취소 콜백 호출
            }
          }} 
          color="error"
          variant="outlined"
        >
          재방송 취소
        </Button> */}

        {/* 오른쪽: 일반 액션 버튼 */}
        <Box>
          <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>창 닫기</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!selectedVideo}
            sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#388e3c" } }}
          >
            선택 완료
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ReplaySelectionDialog;